import type { ApiClientError } from '@contract-analyzer/api-client';

export type AuthRequiredEvent = {
  readonly error: ApiClientError;
};

export type AuthLifecycleEvent = {
  readonly source: 'local' | 'remote';
};

type AuthRequiredListener = (event: AuthRequiredEvent) => void;
type AuthRequiredRecoveryListener = (
  event: AuthRequiredEvent,
) => boolean | Promise<boolean>;
type AuthLifecycleListener = (event: AuthLifecycleEvent) => void;
type AuthBroadcastEventType = 'auth-renewed' | 'auth-failed' | 'auth-signed-out';
type AuthBroadcastMessage = {
  readonly createdAt: number;
  readonly id: string;
  readonly type: AuthBroadcastEventType;
};

const AUTH_BROADCAST_CHANNEL = 'contract-analyzer:auth-session';
const AUTH_BROADCAST_STORAGE_KEY = 'contract-analyzer:auth-session-event';

const authRequiredListeners = new Set<AuthRequiredListener>();
const authRecoveryListeners = new Set<AuthRequiredRecoveryListener>();
const authRenewedListeners = new Set<AuthLifecycleListener>();
const authFailedListeners = new Set<AuthLifecycleListener>();
const authSignedOutListeners = new Set<AuthLifecycleListener>();
const seenBroadcastIds = new Set<string>();

let broadcastChannel: BroadcastChannel | null = null;
let crossTabListenersInitialized = false;
let broadcastSequence = 0;

function notifyLifecycle(
  listeners: ReadonlySet<AuthLifecycleListener>,
  event: AuthLifecycleEvent,
): void {
  for (const listener of listeners) {
    listener(event);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readBroadcastMessage(value: unknown): AuthBroadcastMessage | null {
  if (!isRecord(value)) {
    return null;
  }

  if (
    typeof value['createdAt'] !== 'number' ||
    typeof value['id'] !== 'string' ||
    (
      value['type'] !== 'auth-renewed' &&
      value['type'] !== 'auth-failed' &&
      value['type'] !== 'auth-signed-out'
    )
  ) {
    return null;
  }

  return {
    createdAt: value['createdAt'],
    id: value['id'],
    type: value['type'],
  };
}

function notifyLifecycleByType(type: AuthBroadcastEventType): void {
  switch (type) {
    case 'auth-renewed':
      notifyLifecycle(authRenewedListeners, { source: 'remote' });
      return;
    case 'auth-failed':
      notifyLifecycle(authFailedListeners, { source: 'remote' });
      return;
    case 'auth-signed-out':
      notifyLifecycle(authSignedOutListeners, { source: 'remote' });
      return;
  }
}

function rememberBroadcastId(id: string): boolean {
  if (seenBroadcastIds.has(id)) {
    return false;
  }

  seenBroadcastIds.add(id);
  if (seenBroadcastIds.size > 100) {
    seenBroadcastIds.clear();
    seenBroadcastIds.add(id);
  }
  return true;
}

function handleBroadcastPayload(payload: unknown): void {
  const message = readBroadcastMessage(payload);
  if (message === null || !rememberBroadcastId(message.id)) {
    return;
  }

  notifyLifecycleByType(message.type);
}

function handleStorageEvent(event: StorageEvent): void {
  if (event.key !== AUTH_BROADCAST_STORAGE_KEY || event.newValue === null) {
    return;
  }

  try {
    handleBroadcastPayload(JSON.parse(event.newValue));
  } catch (error) {
    if (error instanceof SyntaxError) {
      console.warn('Ignoring malformed auth session broadcast.', error);
      return;
    }

    throw error;
  }
}

function ensureCrossTabListeners(): void {
  if (crossTabListenersInitialized || typeof window === 'undefined') {
    return;
  }

  crossTabListenersInitialized = true;
  window.addEventListener('storage', handleStorageEvent);

  if (typeof BroadcastChannel !== 'undefined') {
    broadcastChannel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
    broadcastChannel.onmessage = (event: MessageEvent<unknown>) => {
      handleBroadcastPayload(event.data);
    };
  }
}

function createBroadcastMessage(type: AuthBroadcastEventType): AuthBroadcastMessage {
  broadcastSequence += 1;
  return {
    createdAt: Date.now(),
    id: `${Date.now()}:${broadcastSequence}:${Math.random().toString(36).slice(2)}`,
    type,
  };
}

function broadcastLifecycle(type: AuthBroadcastEventType): void {
  if (typeof window === 'undefined') {
    return;
  }

  ensureCrossTabListeners();
  const message = createBroadcastMessage(type);
  rememberBroadcastId(message.id);

  if (broadcastChannel !== null) {
    broadcastChannel.postMessage(message);
  }

  try {
    window.localStorage.setItem(AUTH_BROADCAST_STORAGE_KEY, JSON.stringify(message));
    window.localStorage.removeItem(AUTH_BROADCAST_STORAGE_KEY);
  } catch (error) {
    console.warn('Auth session broadcast could not be persisted for sibling tabs.', error);
  }
}

export async function requestAuthRecovery(error: ApiClientError): Promise<boolean> {
  const event: AuthRequiredEvent = { error };
  for (const listener of authRequiredListeners) {
    listener(event);
  }

  const results = await Promise.all(
    [...authRecoveryListeners].map(async (listener) => await listener(event)),
  );
  return results.some((result) => result);
}

export function subscribeAuthRequired(listener: AuthRequiredListener): () => void {
  authRequiredListeners.add(listener);
  return () => {
    authRequiredListeners.delete(listener);
  };
}

export function subscribeAuthRecovery(listener: AuthRequiredRecoveryListener): () => void {
  authRecoveryListeners.add(listener);
  return () => {
    authRecoveryListeners.delete(listener);
  };
}

export function publishAuthRenewed(): void {
  notifyLifecycle(authRenewedListeners, { source: 'local' });
  broadcastLifecycle('auth-renewed');
}

export function subscribeAuthRenewed(listener: AuthLifecycleListener): () => void {
  ensureCrossTabListeners();
  authRenewedListeners.add(listener);
  return () => {
    authRenewedListeners.delete(listener);
  };
}

export function publishAuthFailed(): void {
  notifyLifecycle(authFailedListeners, { source: 'local' });
  broadcastLifecycle('auth-failed');
}

export function subscribeAuthFailed(listener: AuthLifecycleListener): () => void {
  ensureCrossTabListeners();
  authFailedListeners.add(listener);
  return () => {
    authFailedListeners.delete(listener);
  };
}

export function publishAuthSignedOut(): void {
  notifyLifecycle(authSignedOutListeners, { source: 'local' });
  broadcastLifecycle('auth-signed-out');
}

export function subscribeAuthSignedOut(listener: AuthLifecycleListener): () => void {
  ensureCrossTabListeners();
  authSignedOutListeners.add(listener);
  return () => {
    authSignedOutListeners.delete(listener);
  };
}
